<template>
  <div>
    <div v-if="loading">
      <div class="container">
        <div class="center">
          <div class="loader"></div>
        </div>
      </div>
    </div>
    <div v-else-if="isExpired">
      <div class="container">
        <div class="center">
          <p>EXPIRED LINK</p>
        </div>
      </div>
    </div>

    <div v-else-if="!isValid">
      <div class="container">
        <div class="center">
          <p>LINK NOT AVAILABLE</p>
        </div>
      </div>
    </div>

    <div v-else-if="serverError">
      <div class="container">
        <div class="center">
          <p>SERVER ERROR</p>
        </div>
      </div>
    </div>

    <div v-else>
      <l-map
        ref="peta"
        :center.sync="map.center"
        :zoom="map.zoom"
        :options="mapOptions"
        style="height: 100vh"
      >
        <l-control-polyline-measure
          :options="{ showUnitControl: false }"
          position="topleft"
        />
        <l-control-layers position="topright"></l-control-layers>

        <l-tile-layer
          v-for="tileProvider in tileProviders"
          :key="tileProvider.name"
          :name="tileProvider.name"
          :visible="tileProvider.visible"
          :url="tileProvider.url"
          :attribution="tileProvider.attribution"
          layer-type="base"
        />

        <l-rotated-marker
          v-for="(data, index) in markers"
          v-bind:key="index"
          :index="index"
          :lat-lng="data.position"
          :icon="data.icon"
          :rotationAngle="data.angle"
          :visible="data.visible"
        >
          <l-tooltip
            :options="{
              permanent: true,
              interactive: false,
              direction: 'right'
            }"
            >{{ data.deviceName }}</l-tooltip
          >

          <l-popup :options="{}">
            <div class="leaflet-popup-header">Device Detail</div>
            <table class="leaflet-popup-table">
              <tr>
                <td style="width: 48%">IMEI</td>
                <td>{{ data.imei }}</td>
              </tr>
              <tr>
                <td>License Plate</td>
                <td>{{ data.plate }}</td>
              </tr>
              <!-- <tr>
								<td>Alarm</td>
								<td>{{ item.alarm }}</td>
							</tr> -->
              <tr>
                <td>ACC</td>
                <td>{{ data.acc }}</td>
              </tr>
              <tr>
                <td>Door Status</td>
                <td>{{ data.door == 1 ? 'Door Open' : 'Door Close' }}</td>
              </tr>
              <tr>
                <td>Speed</td>
                <td>{{ data.speed }}</td>
              </tr>
              <tr v-if="data.temperature">
                <td>Temperature</td>
                <td>{{ data.temperature }}&deg;C</td>
              </tr>
              <tr>
                <td>Battery</td>
                <td>{{ data.battery }}%</td>
              </tr>
              <!-- <tr>
								<td>Mileage</td>
								<td>{{ (parseInt(data.mileage)/1000).toFixed(2).toString().replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ".") }} Km</td>
							</tr> -->
              <tr>
                <td>Address</td>
                <td>{{ data.address }}</td>
              </tr>
              <tr>
                <td>URL</td>
                <td>
                  <a :href="data.street_view" target="_blank"
                    >Open Google Maps</a
                  >
                </td>
              </tr>
              <tr>
                <td>Last Positioning</td>
                <td>{{ data.time }}</td>
              </tr>
              <tr>
                <td>Last Update</td>
                <td>{{ data.serverTime }}</td>
              </tr>
            </table>

            <div class="leaflet-popup-footer"></div>
          </l-popup>
        </l-rotated-marker>
      </l-map>
    </div>
  </div>
</template>

<style scoped>
.container {
  position: relative;
  height: 100vh;
}

.container .center {
  position: absolute;
  left: 50%;
  top: 50%;
  margin-right: -50%;
  transform: translate(-50%, -50%);
}

.container .center p {
  font-size: 16px;
  font-weight: bold;
}

.loader {
  border: 3px solid #f3f3f3;
  border-radius: 50%;
  border-top: 3px solid black;
  width: 50px;
  height: 50px;
  -webkit-animation: spin 1s linear infinite; /* Safari */
  animation: spin 1s linear infinite;
}

/* Safari */
@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>

<script>
import L from 'leaflet'
import {
  LMap,
  LTileLayer,
  LMarker,
  LTooltip,
  LPopup,
  LControlLayers
} from 'vue2-leaflet'
import LControlPolylineMeasure from 'vue2-leaflet-polyline-measure'
import LRotatedMarker from 'vue2-leaflet-rotatedmarker'

import { Icon } from 'leaflet'

import API from '@/API.js'
import axios from 'axios'

import 'leaflet/dist/leaflet.css'

delete Icon.Default.prototype._getIconUrl

Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowSize: [0, 0],
  // shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [20, 30]
})

export default {
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LPopup,
    LControlLayers,
    LControlPolylineMeasure,
    LRotatedMarker
  },

  data() {
    return {
      tileProviders: [
        {
          name: 'Google Street',
          visible: true,
          url: 'https://www.google.com/maps/vt?lyrs=m@189&gl=en&x={x}&y={y}&z={z}',
          attribution: 'google'
        },
        {
          name: 'Google Satellites',
          visible: false,
          url: 'https://www.google.com/maps/vt?lyrs=s@189&gl=en&x={x}&y={y}&z={z}',
          attribution: 'google'
        },
        {
          name: 'Google Hybrid',
          visible: false,
          url: 'https://www.google.com/maps/vt?lyrs=s,h@189&gl=en&x={x}&y={y}&z={z}',
          attribution: 'google'
        },
        {
          name: 'Google Terrain',
          visible: false,
          url: 'https://www.google.com/maps/vt?lyrs=p@189&gl=en&x={x}&y={y}&z={z}',
          attribution: 'google'
        },
        {
          name: 'OpenStreetMap',
          visible: false,
          attribution:
            '&copy; <a target="_blank" href="https://osm.org/copyright">OpenStreetMap</a> contributors',
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        }
      ],
      map: {
        center: L.latLng(-6.21011, 106.832459),
        zoom: 13
      },
      mapOptions: {
        // zoomSnap: 0.5
      },

      isExpired: false,
      isValid: true,
      loading: true,
      serverError: false,

      markers: [],
      now: new Date().getTime() / 1000,

      ws: '',
      isConnect: 'open',
      allVehicle: ''
      // interval: null,
    }
  },

  computed: {
    key() {
      return this.$route.params.key
    },

    imei() {
      return this.$route.params.imei
    }
  },

  watch: {
    now() {
      if (this.markers.length > 0) {
        if (this.now > Date.parse(this.markers[0].expired_date) / 1000)
          this.isExpired = true
      }
    }
  },

  created() {
    window.addEventListener('beforeunload', () => {
      if (this.ws) {
        this.ws.send('REMOVE_CLIENT')
        this.ws.close()
      }
    })
  },

  beforeDestroy() {
    if (this.ws) {
      this.ws.send('REMOVE_CLIENT')
      this.ws.close()
    }
  },

  mounted() {
    // this.interval = setInterval(() => {
    // 	if(this.key.length > 0) {
    // 		clearInterval(this.interval)
    // 	}
    // }, 1000)

    this.$nextTick(() => {
      setInterval(() => {
        this.now = new Date().getTime() / 1000
      }, 1000)

      this.initLocation()
      this.connect()
    })
  },

  methods: {
    initLocation() {
      axios
        .get(`${API.base_url}/api/share_location/check/${this.key}`, {}, {})
        .then((res) => {
          const d = res.data.message.data

          this.loading = false
          // if (this.now > Date.parse(d.expired_date) / 1000)
          if (d.is_expired) this.isExpired = true
          else {
            const data = d.gps_data

            let type = ''
            if (Date.now() - new Date(data.server_time).getTime() >= 86400000) {
              type = 'lost'
            } else {
              if (data.alert != 0) type = 'alarm'
              else {
                if (
                  data.acc === 1 ||
                  (data.speed > 0 &&
                    Date.now() - new Date(data.time).getTime() <= 600)
                ) {
                  type = 'acc_on'
                } else type = 'parking'
              }
            }

            let icon = L.Icon.Default

            if (data.ref_icon)
              icon = L.icon({
                iconUrl: `${data.ref_icon[type]}`,
                iconSize: [32, 32]
              })

            const x = {
              expired_date: d.expired_date,
              visible: true,
              imei: data.imei,
              plate: data.plate,
              deviceName: d.name,
              position: L.latLng(data.lat, data.lon),
              angle: data.angle,
              icon: icon,
              acc: data.acc === 1 ? 'ON' : 'OFF',
              gsm: data.gsm_no,
              latLon: `${data.lon}, ${data.lat}`,
              alarm: data.ref_alert ? data.ref_alert.description : 'None',
              speed: `${data.speed} Km/H`,
              mileage: data.mileage,
              time: data.time,
              door: data.door,
              temperature: data.temperature,
              serverTime: data.server_time,
              address: data.address,
              battery: data.battery,
              gps_type: data.gps_type,
              ref_icon: data.ref_icon,
              street_view: `https://maps.google.com/maps?q=${data.lat},${data.lon}&hl=en&z=14&amp;output=embed`
            }

            if (this.markers.length == 0) this.markers.push(x)
            else this.markers[0] = x

            this.newWsClient(data.imei)
            this.allVehicle = data.imei

            this.map.center = L.latLng(data.lat, data.lon)
          }
        })
        .catch((error) => {
          this.loading = false
          if (error.response != undefined && error.response.status == 400)
            this.isValid = false
          else {
            this.serverError = true
          }
        })
    },

    newWsClient(imei) {
      this.inteval = setInterval(() => {
        // if (this.readyState) {
        this.ws.send('NEW_CLIENT:' + imei)
        clearInterval(this.inteval)
        // }
      }, 1000)
    },

    secondToDateTime(second) {
      let d = new Date(parseInt(second) * 1000)

      return d.toISOString().replace('T', ' ').replace('.000Z', '')
    },

    getAddress(latlng, target, index = 0) {
      if (!this[target][index].address) {
        axios
          .get(`${API.base_url}/api/address`, {
            params: {
              latitude: latlng.lat,
              longitude: latlng.lon
            }
          })
          .then((res) => {
            this[target][index].address = res.data.message
          })
      }
    },

    connect() {
      this.ws = new WebSocket(`${API.socket}`)

      this.ws.onopen = (e) => {
        if (this.allVehicle) {
          // this.ws.send('NEW_CLIENT:' + this.selected.join(','))
          this.ws.send('NEW_CLIENT:' + this.allVehicle)

          this.isConnect = 'close'
          if (this.ws.send) {
            this.isConnect = 'open'
            console.log('Socket is open...')
          }
        }
      }

      this.ws.onmessage = (msg) => {
        const data = JSON.parse(msg.data)

        let i = -1
        this.markers.map((item, index) => {
          if (item.imei === data.imei) i = index
        })

        if (i !== -1) {
          if (data.type === 'location') {
            if (data.address == '')
              this.getAddress(
                { lat: data.latitude, lon: data.longitude },
                'markers',
                i
              )
            else this.markers[i].address = data.address
            let type = ''

            if (Date.now() - parseInt(data.time) * 1000 >= 86400000) {
              type = 'lost'
            } else {
              if (data.alarm != 0) type = 'alarm'
              else {
                if (
                  data.acc == 1 ||
                  (data.speed > 0 &&
                    Date.now() - parseInt(data.time) * 1000 <= 600)
                ) {
                  type = 'acc_on'
                } else type = 'parking'
              }
            }

            const color = {
              parking: 'black',
              acc_on: 'green',
              alarm: 'red',
              lost: 'yellow'
            }

            if (this.markers[i].ref_icon) {
              this.markers[i].icon = L.icon({
                // iconUrl: `${API.base_url}/uploads/icon/${this.markers[i].ref_icon[type]}`,
                iconUrl: `${this.markers[i].ref_icon[type]}`,
                iconSize: [32, 32]
              })
            }

            this.markers[i].position = L.latLng(data.latitude, data.longitude)
            this.markers[i].angle = parseInt(data.angle)
            this.markers[i].acc = parseInt(data.acc) === 0 ? 'OFF' : 'ON'
            this.markers[i].latLon = `${data.longitude}, ${data.latitude}`
            this.markers[i].speed = `${data.speed} Km/H`
            this.markers[i].door = data.door
            this.markers[i].temperature = data.temperature
            this.markers[i].mileage = data.mileage
            this.markers[i].battery = data.voltage
            this.markers[i].time = this.secondToDateTime(data.time)
            this.markers[i].serverTime = this.secondToDateTime(data.time)
            this.markers[
              i
            ].street_view = `https://maps.google.com/maps?q=${data.latitude},${data.longitude}&hl=es&z=14&amp;output=embed`
          } else if (data.type === 'heartbeat') {
            this.markers[i].serverTime = this.secondToDateTime(data.time)
            this.markers[i].acc = data.acc == 1 ? 'ON' : 'OFF'

            let type = this.markers[i].type

            if (
              data.acc == 1 ||
              (data.speed > 0 && Date.now() - parseInt(data.time) * 1000 <= 600)
            ) {
              type = 'acc_on'
            } else if (type != 'alarm') type = 'parking'

            const color = {
              parking: 'black',
              acc_on: 'green',
              alarm: 'red',
              lost: 'yellow'
            }

            if (this.markers[i].ref_icon) {
              this.markers[i].icon = L.icon({
                iconUrl: `${API.base_url}/uploads/icon/${this.markers[i].ref_icon[type]}`,
                iconSize: [32, 32]
              })
            }
          } else if (data.type === 'door') {
            this.markers[i].serverTime = this.secondToDateTime(data.time)
            this.markers[i].door = data.door
          } else if (data.type === 'temperature') {
            this.markers[i].serverTime = this.secondToDateTime(data.time)
            this.markers[i].temperature = data.temperature
          }
        }
      }

      this.ws.onclose = (e) => {
        this.isConnect = 'close'
        if (this.$route.name == 'share_location') {
          if (this.isConnect == 'close') {
            setTimeout(() => {
              this.connect()
            }, 10000)
          }

          console.log(
            'Socket is closed. Reconnect will be attempted in 10 second.'
          )
        }
      }

      this.ws.onerror = (err) => {
        this.isConnect = 'close'
        console.error('Socket encountered error: ', 'Closing socket')
        this.ws.close()
      }
    }
  }
}
</script>
